import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  styled,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Popover,
  TextField,
  InputAdornment,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import DynamicTable, { DynamicColumn } from '../ui/DynamicTable';
import CustomAccordion from '../ui/CustomAccordion';
import { STATUS } from 'components/LeadManagement/constants';
import {
  AUDIT_FIELD_KEYS,
  isStatusField,
  isTimestampField,
  isUserField,
  isEventDetailsField,
} from './auditChangesConstants';
import type { AuditChange } from '../types';

interface AuditChangesSectionProps {
  auditChanges?: AuditChange[];
  expandedSection: 'deduplication' | 'comments' | 'audit' | 'caregiver' | 'tracking' | null;
  setExpandedSection: (
    section: 'deduplication' | 'comments' | 'audit' | 'caregiver' | 'tracking' | null
  ) => void;
  fieldConfig?: Array<{ key: string; label: string }>;
  resetFilterText?: string;
}

const TableWrapper = styled(Box)({
  overflowX: 'auto',
  mt: 0,
});

const StyledChip = styled(Chip)<{ actionType: string }>(({ actionType }) => {
  const getActionColor = (action: string) => {
    if (!action) {
      return { bg: '#F5F5F5', color: '#616161' };
    }
    switch (action.toLowerCase()) {
      case 'success':
        return { bg: '#E8F5E9', color: '#2E7D32' };
      case 'processed':
      case 'complete':
      case 'completed':
        return { bg: '#E3F2FD', color: '#1565C0' };
      case 'pending':
      case 'in progress':
      case 'processing':
        return { bg: '#FFF3E0', color: '#E65100' };
      case 'failed':
      case 'error':
      case 'rejected':
        return { bg: '#FFEBEE', color: '#C62828' };
      case STATUS.MANUAL_REVIEW_REQUIRED.toLowerCase():
        return { bg: '#FFF3E0', color: '#E65100' };
      case STATUS.AUTO_MERGE.toLowerCase():
        return { bg: '#E3F2FD', color: '#1565C0' };
      case STATUS.NEW_RECORD.toLowerCase():
        return { bg: '#E8F5E9', color: '#2E7D32' };
      default:
        return { bg: '#F5F5F5', color: '#616161' };
    }
  };

  const colors = getActionColor(actionType);
  return {
    backgroundColor: colors.bg,
    color: colors.color,
    fontSize: '11px',
    fontWeight: 600,
    height: '22px',
    textTransform: 'capitalize',
  };
});

const UserCell = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  color: theme.palette.neutral8?.main,
  fontWeight: 500,
}));

const TimestampCell = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  color: theme.palette.neutral7?.main,
}));

function auditColumnMenuMode(columnId: string): 'filter' | 'sortOnly' | null {
  const k = columnId.toLowerCase();
  if (k === AUDIT_FIELD_KEYS.LEAD_ID.toLowerCase()) return 'filter';
  if (k === AUDIT_FIELD_KEYS.EVENT_TYPE.toLowerCase()) return 'filter';
  if (k === AUDIT_FIELD_KEYS.TIMESTAMP.toLowerCase()) return 'sortOnly';
  if (k === AUDIT_FIELD_KEYS.USER.toLowerCase()) return 'filter';
  return null;
}

function getAuditColumnFilterHaystack(row: AuditChange, columnId: string): string {
  const k = columnId.toLowerCase();
  if (k === AUDIT_FIELD_KEYS.LEAD_ID.toLowerCase()) return String(row.leadId ?? '');
  if (k === AUDIT_FIELD_KEYS.EVENT_TYPE.toLowerCase()) return row.eventType ?? '';
  if (k === AUDIT_FIELD_KEYS.USER.toLowerCase()) return row.user ?? '';
  return '';
}

function compareAuditRows(
  a: AuditChange,
  b: AuditChange,
  columnId: string,
  order: 'asc' | 'desc'
): number {
  const mult = order === 'asc' ? 1 : -1;
  const k = columnId.toLowerCase();
  if (k === AUDIT_FIELD_KEYS.LEAD_ID.toLowerCase()) {
    const na = Number(a.leadId);
    const nb = Number(b.leadId);
    const va = Number.isFinite(na) ? na : 0;
    const vb = Number.isFinite(nb) ? nb : 0;
    return mult * (va - vb);
  }
  if (k === AUDIT_FIELD_KEYS.TIMESTAMP.toLowerCase()) {
    const ta = new Date(a.timeStamp).getTime() || 0;
    const tb = new Date(b.timeStamp).getTime() || 0;
    return mult * (ta - tb);
  }
  const sa = (getAuditColumnFilterHaystack(a, columnId) || '').toLowerCase();
  const sb = (getAuditColumnFilterHaystack(b, columnId) || '').toLowerCase();
  return mult * sa.localeCompare(sb);
}

interface AuditTableColumnHeaderProps {
  label: string;
  menuMode: 'filter' | 'sortOnly';
  filterText: string;
  onFilterTextChange: (value: string) => void;
  activeSort: 'asc' | 'desc' | null;
  onSortAsc: () => void;
  onSortDesc: () => void;
}

const AuditTableColumnHeader: React.FC<AuditTableColumnHeaderProps> = ({
  label,
  menuMode,
  filterText,
  onFilterTextChange,
  activeSort,
  onSortAsc,
  onSortDesc,
}) => {
  const iconRef = useRef<HTMLButtonElement>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [filterOpen, setFilterOpen] = useState(false);

  const openMenu = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  };

  const closeMenu = () => setMenuAnchor(null);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 0.25,
        width: '100%',
        minWidth: 0,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <Typography
        component="span"
        sx={{ fontSize: 'inherit', fontWeight: 600, flex: '1 1 auto', minWidth: 0 }}
        noWrap
        title={label}
      >
        {label}
      </Typography>
      <IconButton
        ref={iconRef}
        size="small"
        aria-label={`${label} column options`}
        sx={{ p: 0.25, flexShrink: 0 }}
        onClick={openMenu}
      >
        <MoreVertIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            onSortDesc();
            closeMenu();
          }}
          selected={activeSort === 'desc'}
          dense
        >
          Sort by descending
        </MenuItem>
        <MenuItem
          onClick={() => {
            onSortAsc();
            closeMenu();
          }}
          selected={activeSort === 'asc'}
          dense
        >
          Sort by ascending
        </MenuItem>
        {menuMode === 'filter' ? (
          <MenuItem
            onClick={() => {
              closeMenu();
              setFilterOpen(true);
            }}
            dense
          >
            Filter by values
          </MenuItem>
        ) : null}
      </Menu>
      <Popover
        open={filterOpen}
        anchorEl={iconRef.current}
        onClose={() => setFilterOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 1.5, minWidth: 220 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search in column…"
            value={filterText}
            onChange={(e) => onFilterTextChange(e.target.value)}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            variant="outlined"
          />
        </Box>
      </Popover>
    </Box>
  );
};

const auditLeadIdColumn: DynamicColumn<AuditChange> = {
  id: AUDIT_FIELD_KEYS.LEAD_ID,
  label: 'Lead Id',
  accessor: AUDIT_FIELD_KEYS.LEAD_ID,
  width: '88px',
  render: (row) => (
    <Typography fontSize="12px">{row.leadId != null ? String(row.leadId) : '-'}</Typography>
  ),
};

const AuditChangesSection: React.FC<AuditChangesSectionProps> = ({
  auditChanges,
  expandedSection,
  setExpandedSection,
  fieldConfig,
  resetFilterText,
}) => {
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [sortState, setSortState] = useState<{ columnId: string; order: 'asc' | 'desc' } | null>(
    null
  );

  const formatAuditTimestamp = useCallback((dateValue: string) => {
    let formattedDate = '-';
    if (dateValue) {
      const date = new Date(dateValue);
      const getTimeZoneAbbr = (d: Date) => {
        try {
          const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          const parts = new Intl.DateTimeFormat('en-US', {
            timeZone,
            timeZoneName: 'short',
          }).formatToParts(d);
          return parts.find((p) => p.type === 'timeZoneName')?.value || '';
        } catch {
          return '';
        }
      };

      const pad = (n: number) => n.toString().padStart(2, '0');
      const month = pad(date.getMonth() + 1);
      const day = pad(date.getDate());
      const year = date.getFullYear();

      let hours = date.getHours();
      const minutes = pad(date.getMinutes());
      const seconds = pad(date.getSeconds());
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12;

      const tzAbbr = getTimeZoneAbbr(date);

      formattedDate = `${month}-${day}-${year}, ${hours}:${minutes}:${seconds} ${ampm} ${tzAbbr}`;
    }
    return <TimestampCell>{formattedDate}</TimestampCell>;
  }, []);

  const baseColumns: DynamicColumn<AuditChange>[] = useMemo(() => {
    const defaultColumns: DynamicColumn<AuditChange>[] = [
      auditLeadIdColumn,
      {
        id: AUDIT_FIELD_KEYS.EVENT_DETAILS,
        label: 'Event Details',
        accessor: AUDIT_FIELD_KEYS.EVENT_DETAILS,
        width: '250px',
        render: (row) => <Typography fontSize="12px">{row.eventDetails}</Typography>,
      },
      {
        id: AUDIT_FIELD_KEYS.EVENT_STATUS,
        label: 'Event Status',
        accessor: AUDIT_FIELD_KEYS.EVENT_STATUS,
        render: (row) => (
          <StyledChip label={row.eventStatus} size="small" actionType={row.eventStatus} />
        ),
      },
      {
        id: AUDIT_FIELD_KEYS.EVENT_TYPE,
        label: 'Event Type',
        accessor: AUDIT_FIELD_KEYS.EVENT_TYPE,
        render: (row) => <Typography fontSize="12px">{row.eventType}</Typography>,
      },
      {
        id: AUDIT_FIELD_KEYS.OLD_DEDUPE_STATUS,
        label: 'Old Status',
        accessor: AUDIT_FIELD_KEYS.OLD_DEDUPE_STATUS,
        render: (row) => (
          <StyledChip label={row.oldDeDupeStatus} size="small" actionType={row.oldDeDupeStatus} />
        ),
      },
      {
        id: AUDIT_FIELD_KEYS.NEW_DEDUPE_STATUS,
        label: 'New Status',
        accessor: AUDIT_FIELD_KEYS.NEW_DEDUPE_STATUS,
        render: (row) => (
          <StyledChip label={row.newDeDupeStatus} size="small" actionType={row.newDeDupeStatus} />
        ),
      },
      {
        id: AUDIT_FIELD_KEYS.PROCESS_TYPE,
        label: 'Process Type',
        accessor: AUDIT_FIELD_KEYS.PROCESS_TYPE,
        render: (row) => <Typography fontSize="12px">{row.processType}</Typography>,
      },
      {
        id: AUDIT_FIELD_KEYS.PROCESS_STATUS,
        label: 'Process Status',
        accessor: AUDIT_FIELD_KEYS.PROCESS_STATUS,
        render: (row) => (
          <StyledChip label={row.processStatus} size="small" actionType={row.processStatus} />
        ),
      },
      {
        id: AUDIT_FIELD_KEYS.TIMESTAMP,
        label: 'Timestamp',
        accessor: AUDIT_FIELD_KEYS.TIMESTAMP,
        render: (row) => <TimestampCell>{row.timeStamp}</TimestampCell>,
      },
      {
        id: AUDIT_FIELD_KEYS.USER,
        label: 'User',
        accessor: AUDIT_FIELD_KEYS.USER,
        render: (row) => <UserCell>{row.user}</UserCell>,
      },
    ];

    if (fieldConfig && fieldConfig.length > 0) {
      const hasLeadIdField = fieldConfig.some(
        (field) => field.key.toLowerCase() === AUDIT_FIELD_KEYS.LEAD_ID.toLowerCase()
      );
      const configColumns = fieldConfig.map((field) => {
        const column: DynamicColumn<AuditChange> = {
          id: field.key,
          label: field.label,
          accessor: field.key as keyof AuditChange,
        };

        if (isEventDetailsField(field.key)) {
          column.width = '250px';
        }

        if (isTimestampField(field.key)) {
          column.render = (row) => formatAuditTimestamp(row.timeStamp);
        } else if (isUserField(field.key)) {
          column.render = (row) => <UserCell>{row.user}</UserCell>;
        } else if (isStatusField(field.key)) {
          column.render = (row) => {
            const value = (row as any)[field.key] || '-';
            return <StyledChip label={value} size="small" actionType={value} />;
          };
        } else {
          column.render = (row) => (
            <Typography fontSize="12px">{(row as any)[field.key] ?? '-'}</Typography>
          );
        }
        return column;
      });
      return hasLeadIdField ? configColumns : [auditLeadIdColumn, ...configColumns];
    }

    return defaultColumns.map((col) => {
      if (col.id === AUDIT_FIELD_KEYS.TIMESTAMP) {
        return {
          ...col,
          render: (row: AuditChange) => formatAuditTimestamp(row.timeStamp),
        };
      }
      return col;
    });
  }, [fieldConfig, formatAuditTimestamp]);

  const columns = useMemo(() => {
    return baseColumns.map((col) => {
      const mode = auditColumnMenuMode(col.id);
      if (!mode) {
        return col;
      }
      return {
        ...col,
        renderHeader: () => (
          <AuditTableColumnHeader
            label={col.label}
            menuMode={mode}
            filterText={columnFilters[col.id] ?? ''}
            onFilterTextChange={(value) =>
              setColumnFilters((prev) => {
                const next = { ...prev, [col.id]: value };
                if (!value.trim()) {
                  delete next[col.id];
                }
                return next;
              })
            }
            activeSort={sortState?.columnId === col.id ? sortState.order : null}
            onSortAsc={() => setSortState({ columnId: col.id, order: 'asc' })}
            onSortDesc={() => setSortState({ columnId: col.id, order: 'desc' })}
          />
        ),
      };
    });
  }, [baseColumns, columnFilters, sortState]);

  const tableData = useMemo(() => {
    let rows = [...(auditChanges || [])];

    const activeFilters = Object.entries(columnFilters).filter(([, v]) => v?.trim());
    for (const [colId, raw] of activeFilters) {
      const needle = raw.trim().toLowerCase();
      rows = rows.filter((row) =>
        getAuditColumnFilterHaystack(row, colId).toLowerCase().includes(needle)
      );
    }

    if (sortState) {
      rows = [...rows].sort((a, b) => compareAuditRows(a, b, sortState.columnId, sortState.order));
    }

    return rows;
  }, [auditChanges, columnFilters, sortState]);

  const hasActiveColumnFilters = useMemo(
    () => Object.values(columnFilters).some((v) => Boolean(v?.trim())),
    [columnFilters]
  );
  const hasActiveFilterOrSort = hasActiveColumnFilters || sortState !== null;

  const handleResetFilters = useCallback(() => {
    setColumnFilters({});
    setSortState(null);
  }, []);

  return (
    <CustomAccordion
      title="Audit Changes"
      count={auditChanges?.length}
      expanded={expandedSection === 'audit'}
      onChange={() => setExpandedSection(expandedSection === 'audit' ? null : 'audit')}
      detailsSx={{ p: 0 }}
    >
      <TableWrapper>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            px: 1,
            pt: 1,
            pb: 0.5,
          }}
        >
          <Button
            size="small"
            variant="text"
            disabled={!hasActiveFilterOrSort}
            onClick={handleResetFilters}
            sx={{
              fontSize: '12px',
              fontWeight: 600,
              textTransform: 'none',
              color: (theme) => theme.palette.themeColor?.main || theme.palette.primary.main,
            }}
          >
            {resetFilterText || 'Reset filters'}
          </Button>
        </Box>
        <DynamicTable
          columns={columns}
          data={tableData}
          variant="compact"
          noDataMessage="No audit changes recorded for this record."
          getRowId={(row) =>
            `${row.leadId ?? ''}-${row.timeStamp}-${row.user}-${row.eventType}-${row.eventDetails}`
          }
        />
      </TableWrapper>
    </CustomAccordion>
  );
};

export type { AuditChange } from '../types';

export default AuditChangesSection;
